<script>
    import { onMount } from 'svelte';
    import { fetchPilotosPeso, searchPilotosPeso, subscribeToPilotosPeso, unsubscribeFromPilotosPeso } from '../controllers/pilotoPesoController.js';
    import CardStats from '../components/Cards/CardStats.svelte';
    import CardPilotoPesoChart from '../components/Cards/CardPilotoPesoChart.svelte';
    import CardPilotoPesoBarChart from '../components/Cards/CardPilotoPesoBarChart.svelte';
    import CardPilotoPesoSummary from '../components/Cards/CardPilotoPesoSummary.svelte';

    let records = [];
    let loading = true;
    let error = null;
    let selectedPiloto = '';
    let sortField = 'fecha';
    let sortDirection = 'desc';
    let currentPage = 1;
    let itemsPerPage = 10;
    let selectedDeliveryMethod = '';

    // New filters for month and year
    let monthFilter = new Date().getMonth(); // 0-based (0=Enero)
    let yearFilter = new Date().getFullYear();

    // Statistics data
    let stats = {
        totalPilotos: 0,
        totalPeso: 0,
        averagePeso: 0,
        topPiloto: '',
        topPeso: 0,
        totalEscoltas: 0,
        minPeso: 0,
        maxPeso: 0,
        totalValue: 0,
        averageValue: 0,
        deliveryMethodBreakdown: { Repartidor: 0, Transporte: 0 }
    };

    // Chart data
    let chartData = {
        labels: [],
        datasets: []
    };

    // Configuración de las columnas para la tabla
    let columns = [
        { label: 'Fecha', field: 'fecha', type: 'date' },
        { label: 'Nombre', field: 'nombre', type: 'text' },
        { label: 'Numero', field: 'numero', type: 'text' },
        { label: 'Destino', field: 'target_ref', type: 'text' },
        { label: 'Transporte', field: 'transporte_descrip', type: 'text' },
        { label: 'Transporte Ref', field: 'transporte_ref', type: 'text' },
        { label: 'Método Entrega', field: 'delivery_method', type: 'text' },
        { label: 'Total Peso', field: 'total_peso', type: 'number' },
        { label: 'Escoltas', field: 'escoltas_count', type: 'number' },
        { label: 'Escoltas Nombres', field: 'escoltas_nombres', type: 'text' },
        { label: 'Total', field: 'total', type: 'number' }
    ];

    // Calculate statistics from records
    function calculateStats(data) {
        if (!data || data.length === 0) {
            return {
                totalPilotos: 0,
                totalPeso: 0,
                averagePeso: 0,
                topPiloto: '',
                topPeso: 0,
                totalEscoltas: 0,
                minPeso: 0,
                maxPeso: 0,
                totalValue: 0,
                averageValue: 0,
                deliveryMethodBreakdown: { Repartidor: 0, Transporte: 0 }
            };
        }

        const totalPeso = data.reduce((sum, record) => sum + (record.total_peso || 0), 0);
        const averagePeso = totalPeso / data.length;
        
        // Calculate escort statistics
        const totalEscoltas = data.reduce((sum, record) => sum + (record.escoltas_count || 0), 0);
        
        // Calculate peso min/max
        const pesos = data.map(record => record.total_peso || 0).filter(peso => peso > 0);
        const minPeso = pesos.length > 0 ? Math.min(...pesos) : 0;
        const maxPeso = pesos.length > 0 ? Math.max(...pesos) : 0;
        
        // Calculate total value and average value
        const totalValue = data.reduce((sum, record) => sum + (record.total || 0), 0);
        const averageValue = totalValue / data.length;
        
        // Delivery method breakdown
        const deliveryMethodBreakdown = data.reduce((acc, record) => {
            const method = record.delivery_method || 'Sin método';
            acc[method] = (acc[method] || 0) + 1;
            return acc;
        }, {});
        
        // Find top piloto by total peso
        const pilotoTotals = {};
        data.forEach(record => {
            if (record.nombre) {
                pilotoTotals[record.nombre] = (pilotoTotals[record.nombre] || 0) + (record.total_peso || 0);
            }
        });
        
        const topPiloto = Object.keys(pilotoTotals).reduce((a, b) => 
            pilotoTotals[a] > pilotoTotals[b] ? a : b, '');
        const topPeso = pilotoTotals[topPiloto] || 0;

        return {
            totalPilotos: data.length,
            totalPeso,
            averagePeso: Math.round(averagePeso * 100) / 100,
            topPiloto,
            topPeso,
            totalEscoltas,
            minPeso,
            maxPeso,
            totalValue: Math.round(totalValue * 100) / 100,
            averageValue: Math.round(averageValue * 100) / 100,
            deliveryMethodBreakdown
        };
    }

    // Prepare chart data
    function prepareChartData(data) {
        if (!data || data.length === 0) {
            return { labels: [], datasets: [] };
        }

        // Group by date and sum total_peso
        const groupedByDate = {};
        data.forEach(record => {
            const recordDate = new Date(record.fecha);
            // Convert from UTC to Guatemala timezone (UTC-6)
            const guatemalaDate = new Date(recordDate.getTime() - (6 * 60 * 60 * 1000));
            const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
            const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
            const year = guatemalaDate.getUTCFullYear();
            const date = `${day}/${month}/${year}`;
            groupedByDate[date] = (groupedByDate[date] || 0) + (record.total_peso || 0);
        });

        const labels = Object.keys(groupedByDate).slice(-10); // Last 10 dates
        const values = labels.map(date => groupedByDate[date]);

        return {
            labels,
            datasets: [{
                label: 'Total Peso por Fecha',
                backgroundColor: '#4c51bf',
                borderColor: '#4c51bf',
                data: values,
                fill: false
            }]
        };
    }

    // Filter records by date
    function filterRecordsByDate(data, filter) {
        if (filter === 'all') return data;
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        return data.filter(record => {
            const recordDate = new Date(record.fecha);
            
            switch (filter) {
                case 'today':
                    return recordDate >= today;
                case 'week':
                    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                    return recordDate >= weekAgo;
                case 'month':
                    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                    return recordDate >= monthAgo;
                default:
                    return true;
            }
        });
    }

    // Update filterRecordsByDate to filter by month and year
    function filterRecordsByMonthYear(data, month, year) {
        return data.filter(record => {
            const recordDate = new Date(record.fecha);
            return recordDate.getMonth() === month && recordDate.getFullYear() === year;
        });
    }

    async function loadData() {
        try {
            loading = true;
            records = await fetchPilotosPeso();
            // Debug: log unique delivery_method values
            console.log('Unique delivery_method values:', [...new Set(records.map(r => r.delivery_method))]);
            error = null;
        } catch (e) {
            error = e.message;
        } finally {
            loading = false;
        }
    }

    async function handleSearch() {
        if (!searchQuery) {
            await loadData();
            return;
        }
        try {
            loading = true;
            records = await searchPilotosPeso(searchQuery);
            error = null;
        } catch (e) {
            error = e.message;
        } finally {
            loading = false;
        }
    }

    // Filter records by piloto
    function filterRecordsByPiloto(data, piloto) {
        if (!piloto) return data;
        return data.filter(record => record.nombre === piloto);
    }

    // Reactive statements to update stats and chart data
    $: dateFilteredRecords = filterRecordsByMonthYear(records, monthFilter, yearFilter);
    $: filteredRecords = filterRecordsByPiloto(dateFilteredRecords, selectedPiloto);
    $: stats = calculateStats(filteredRecords);
    $: chartData = prepareChartData(filteredRecords);

    // Add filter by delivery_method
    function filterRecordsByDeliveryMethod(data, method) {
        if (!method) return data;
        return data.filter(record => record.delivery_method === method);
    }

    $: deliveryFilteredRecords = filterRecordsByDeliveryMethod(filteredRecords, selectedDeliveryMethod);
    // Update stats and chartData to use deliveryFilteredRecords
    $: stats = calculateStats(deliveryFilteredRecords);
    $: chartData = prepareChartData(deliveryFilteredRecords);

    // Sort and paginate records
    function sortRecords(data, field, direction) {
        return [...data].sort((a, b) => {
            let aVal = a[field];
            let bVal = b[field];
            
            if (field === 'fecha') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            
            if (direction === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    }

    function paginateRecords(data, page, perPage) {
        const start = (page - 1) * perPage;
        const end = start + perPage;
        return data.slice(start, end);
    }

    // Get unique pilotos for filter
    $: uniquePilotos = [...new Set(deliveryFilteredRecords.map(r => r.nombre))].filter(Boolean);
    // Use deliveryFilteredRecords as the base for all dashboard elements
    $: sortedRecords = sortRecords(deliveryFilteredRecords, sortField, sortDirection);
    $: paginatedRecords = paginateRecords(sortedRecords, currentPage, itemsPerPage);
    $: totalPages = Math.ceil(sortedRecords.length / itemsPerPage);

    // Get unique delivery methods for filter
    $: uniqueDeliveryMethods = [...new Set(records.map(r => r.delivery_method))].filter(Boolean);

    // Handle sorting
    function handleSort(field) {
        if (sortField === field) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortField = field;
            sortDirection = 'asc';
        }
        currentPage = 1; // Reset to first page when sorting
    }

    // Handle pagination
    function goToPage(page) {
        if (page >= 1 && page <= totalPages) {
            currentPage = page;
        }
    }



    // Export to Excel (XLSX format)
    function exportToExcel() {
        // Define the fields we want to export based on the actual data structure
        const exportFields = [
            'fecha',
            'nombre', 
            'numero',
            'target_ref',
            'transporte_descrip',
            'transporte_ref',
            'delivery_method',
            'total_peso',
            'escoltas_count',
            'escoltas_nombres',
            'total'
        ];
        
        // Create headers for the fields
        const fieldLabels = {
            'fecha': 'Fecha',
            'nombre': 'Nombre',
            'numero': 'Numero',
            'target_ref': 'Destino',
            'transporte_descrip': 'Transporte',
            'transporte_ref': 'Transporte Ref',
            'delivery_method': 'Método Entrega',
            'total_peso': 'Total Peso',
            'escoltas_count': 'Cantidad Escoltas',
            'escoltas_nombres': 'Nombres Escoltas',
            'total': 'Total'
        };

        // Create CSV content (more reliable than XML for Excel)
        let csvContent = '\ufeff'; // BOM for UTF-8
        csvContent += exportFields.map(field => fieldLabels[field] || field).join(',') + '\n';
        
        sortedRecords.forEach(record => {
            const row = exportFields.map(field => {
                let value = record[field];
                
                if (value === null || value === undefined) {
                    return '';
                } else if (field === 'fecha') {
                    const date = new Date(value);
                    // Convert from UTC to Guatemala timezone (UTC-6)
                    const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
                    const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
                    const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
                    const year = guatemalaDate.getUTCFullYear();
                    return `${day}/${month}/${year}`;
                } else if (typeof value === 'number') {
                    return value.toString();
                } else if (typeof value === 'string') {
                    // Escape quotes and wrap in quotes if contains comma
                    const escaped = value.replace(/"/g, '""');
                    return value.includes(',') ? `"${escaped}"` : escaped;
                } else {
                    return value.toString();
                }
            });
            csvContent += row.join(',') + '\n';
        });

        // Create blob with CSV MIME type
        const blob = new Blob([csvContent], { 
            type: 'text/csv;charset=utf-8' 
        });
        
        // Create download link
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `pilotos-peso-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Modal state for escort details
    let showEscoltasModal = false;
    let selectedRecord = null;

    // Function to show escort details
    function showEscoltasDetails(record) {
        selectedRecord = record;
        showEscoltasModal = true;
    }

    // Function to close escort details modal
    function closeEscoltasModal() {
        showEscoltasModal = false;
        selectedRecord = null;
    }

    let subscription;
    onMount(async () => {
        await loadData();
        subscription = subscribeToPilotosPeso(() => {
            loadData();
        });
        return () => {
            if (subscription) {
                unsubscribeFromPilotosPeso(subscription);
            }
        };
    });
</script>

<!-- Header -->
<div class="relative bg-blueGray-800 md:pt-16 pb-32 pt-12">
    <div class="px-4 md:px-10 mx-auto w-full">
        <!-- Filters and Search (moved here) -->
        <div class="w-full mb-6 px-4">
            <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div class="rounded-t mb-0 px-4 py-3 border-0">
                    <div class="flex flex-wrap items-center">
                        <div class="relative w-full px-4 max-w-full flex-grow flex-1">
                            <h3 class="font-semibold text-base text-blueGray-700">
                                Filtros y Búsqueda
                            </h3>
                        </div>
                    </div>
                </div>
                <div class="block w-full overflow-x-auto p-4">
                    <div class="flex flex-wrap gap-4 items-center">
                        <!-- Month Filter -->
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Mes:</label>
                            <select
                                bind:value={monthFilter}
                                class="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={0}>Enero</option>
                                <option value={1}>Febrero</option>
                                <option value={2}>Marzo</option>
                                <option value={3}>Abril</option>
                                <option value={4}>Mayo</option>
                                <option value={5}>Junio</option>
                                <option value={6}>Julio</option>
                                <option value={7}>Agosto</option>
                                <option value={8}>Septiembre</option>
                                <option value={9}>Octubre</option>
                                <option value={10}>Noviembre</option>
                                <option value={11}>Diciembre</option>
                            </select>
                        </div>
                        <!-- Year Filter -->
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Año:</label>
                            <input
                                type="number"
                                bind:value={yearFilter}
                                min="2000"
                                max="2100"
                                class="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                            />
                        </div>
                        <!-- Piloto Filter (unchanged) -->
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Piloto:</label>
                            <select
                                bind:value={selectedPiloto}
                                class="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos</option>
                                {#each uniquePilotos as piloto}
                                    <option value={piloto}>{piloto}</option>
                                {/each}
                            </select>
                        </div>
                        <!-- Delivery Method Filter -->
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Método de entrega:</label>
                            <select
                                bind:value={selectedDeliveryMethod}
                                class="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos</option>
                                <option value="Repartidor">Repartidor</option>
                                <option value="Transporte">Transporte</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- End Filters and Search -->
        <div>
            <!-- Header stats -->
            <div class="flex flex-wrap">
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Total Pedidos"
                        statTitle={stats.totalPilotos.toString()}
                        statArrow="up"
                        statPercent="12"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Desde el mes pasado"
                        statIconName="fas fa-users"
                        statIconColor="bg-red-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Total Peso"
                        statTitle={`${stats.totalPeso.toLocaleString()} lbs`}
                        statArrow="up"
                        statPercent="8.1"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Desde el mes pasado"
                        statIconName="fas fa-weight-hanging"
                        statIconColor="bg-orange-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Peso Promedio"
                        statTitle={`${stats.averagePeso} lbs`}
                        statArrow="down"
                        statPercent="3.48"
                        statPercentColor="text-red-500"
                        statDescripiron="Desde el mes pasado"
                        statIconName="fas fa-chart-line"
                        statIconColor="bg-pink-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Total Escoltas"
                        statTitle={stats.totalEscoltas.toString()}
                        statArrow="up"
                        statPercent="15"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Total de escoltas asignadas"
                        statIconName="fas fa-shield-alt"
                        statIconColor="bg-purple-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Peso Mínimo"
                        statTitle={`${stats.minPeso.toLocaleString()} lbs`}
                        statArrow="down"
                        statPercent="5"
                        statPercentColor="text-red-500"
                        statDescripiron="Peso mínimo registrado"
                        statIconName="fas fa-weight-hanging"
                        statIconColor="bg-orange-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Peso Máximo"
                        statTitle={`${stats.maxPeso.toLocaleString()} lbs`}
                        statArrow="up"
                        statPercent="8"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Peso máximo registrado"
                        statIconName="fas fa-weight-hanging"
                        statIconColor="bg-red-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Valor Total"
                        statTitle={`Q${stats.totalValue.toLocaleString()}`}
                        statArrow="up"
                        statPercent="12"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Valor total de egresos"
                        statIconName="fas fa-dollar-sign"
                        statIconColor="bg-red-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Top Piloto"
                        statTitle={stats.topPiloto || 'N/A'}
                        statArrow="up"
                        statPercent="12"
                        statPercentColor="text-emerald-500"
                        statDescripiron={`${stats.topPeso.toLocaleString()} lbs total`}
                        statIconName="fas fa-trophy"
                        statIconColor="bg-red-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Repartidor"
                        statTitle={stats.deliveryMethodBreakdown.Repartidor || 0}
                        statArrow="up"
                        statPercent="8"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Entregas por repartidor"
                        statIconName="fas fa-motorcycle"
                        statIconColor="bg-yellow-500"
                    />
                </div>
                <div class="w-full lg:w-6/12 xl:w-2/12 px-4">
                    <CardStats
                        statSubtitle="Transporte"
                        statTitle={stats.deliveryMethodBreakdown.Transporte || 0}
                        statArrow="up"
                        statPercent="6"
                        statPercentColor="text-emerald-500"
                        statDescripiron="Entregas por transporte"
                        statIconName="fas fa-truck"
                        statIconColor="bg-indigo-500"
                    />
                </div>
            </div>
        </div>
    </div>
</div>

<!-- Escoltas Details Modal -->
{#if showEscoltasModal && selectedRecord}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-semibold text-gray-900">
                    Detalles de Escoltas - {selectedRecord.nombre}
                </h3>
                <button
                    on:click={closeEscoltasModal}
                    class="text-gray-400 hover:text-gray-600"
                >
                    <i class="fas fa-times text-xl"></i>
                </button>
            </div>
            
            <div class="mb-4">
                <div class="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span class="font-semibold">Fecha:</span> {(() => {
                            const date = new Date(selectedRecord.fecha);
                            // Convert from UTC to Guatemala timezone (UTC-6)
                            const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
                            const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
                            const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
                            const year = guatemalaDate.getUTCFullYear();
                            return `${day}/${month}/${year}`;
                        })()}
                    </div>
                    <div>
                        <span class="font-semibold">Total Peso:</span> {selectedRecord.total_peso.toLocaleString()} lbs
                    </div>
                    <div>
                        <span class="font-semibold">Cantidad de Escoltas:</span> {selectedRecord.escoltas_count}
                    </div>
                    <div>
                        <span class="font-semibold">Total Valor:</span> ${selectedRecord.total.toLocaleString()}
                    </div>
                </div>
            </div>

            {#if selectedRecord.escoltas && selectedRecord.escoltas.length > 0}
                <div class="border-t pt-4">
                    <h4 class="font-semibold mb-3 text-gray-800">Lista de Escoltas:</h4>
                    <div class="overflow-x-auto">
                        <table class="w-full text-sm">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th class="px-4 py-2 text-left font-semibold">Nombre</th>
                                    <th class="px-4 py-2 text-left font-semibold">Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each selectedRecord.escoltas as escolta}
                                    <tr class="border-b hover:bg-gray-50">
                                        <td class="px-4 py-2">{escolta.nombre}</td>
                                        <td class="px-4 py-2 font-semibold">${escolta.valor.toLocaleString()}</td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </div>
            {:else}
                <div class="text-center py-8 text-gray-500">
                    <i class="fas fa-shield-alt text-3xl mb-2"></i>
                    <p>No hay escoltas asignadas a este egreso</p>
                </div>
            {/if}

            <div class="flex justify-end mt-6">
                <button
                    on:click={closeEscoltasModal}
                    class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                    Cerrar
                </button>
            </div>
        </div>
    </div>
{/if}

<div class="px-4 md:px-10 mx-auto w-full -m-24">
    <div class="flex flex-wrap mt-4">
        <!-- Summary Cards -->
        <div class="w-full mb-6 px-4">
            <CardPilotoPesoSummary {records} {stats} />
        </div>

        <!-- Charts Row -->
        <div class="w-full xl:w-8/12 mb-12 xl:mb-0 px-4">
            <CardPilotoPesoChart {chartData} />
        </div>
        <div class="w-full xl:w-4/12 px-4">
            <CardPilotoPesoBarChart records={filteredRecords} />
        </div>

        <!-- Data Table -->
        <div class="w-full mb-12 px-4">
            <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div class="rounded-t mb-0 px-4 py-3 border-0">
                    <div class="flex flex-wrap items-center">
                        <div class="relative w-full px-4 max-w-full flex-grow flex-1">
                            <h3 class="font-semibold text-base text-blueGray-700">
                                Reporte Detallado de Pilotos de Peso
                            </h3>
                        </div>
                        <div class="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
                            <div class="flex items-center space-x-2">
                                <span class="text-sm text-gray-500">
                                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, sortedRecords.length)} de {sortedRecords.length} registros
                                </span>
                                <button
                                    on:click={exportToExcel}
                                    class="bg-blueGray-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <i class="fas fa-file-csv mr-1"></i>
                                    Exportar CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="block w-full overflow-x-auto">
                    {#if loading}
                        <div class="text-center py-8">
                            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <p class="mt-2 text-gray-600">Cargando datos...</p>
                        </div>
                    {:else if error}
                        <div class="text-center py-8 text-red-500">
                            <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
                            <p>{error}</p>
                        </div>
                    {:else if filteredRecords.length === 0}
                        <div class="text-center py-8 text-gray-500">
                            <i class="fas fa-inbox text-2xl mb-2"></i>
                            <p>No se encontraron registros</p>
                        </div>
                    {:else}
                        <table class="items-center w-full bg-transparent border-collapse">
                            <thead>
                                <tr>
                                    {#each columns as column}
                                        <th 
                                            class="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left cursor-pointer hover:bg-blueGray-100"
                                            on:click={() => handleSort(column.field)}
                                        >
                                            <div class="flex items-center">
                                                {column.label}
                                                {#if sortField === column.field}
                                                    <i class="fas fa-sort-{sortDirection === 'asc' ? 'up' : 'down'} ml-1 text-blue-500"></i>
                                                {:else}
                                                    <i class="fas fa-sort ml-1 text-gray-400"></i>
                                                {/if}
                                            </div>
                                        </th>
                                    {/each}

                                </tr>
                            </thead>
                            <tbody>
                                {#each paginatedRecords as record}
                                    <tr class="hover:bg-gray-50">
                                        {#each columns as column}
                                                                                    <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                                            {#if column.type === 'date'}
                                                {(() => {
                                                    const date = new Date(record[column.field]);
                                                    // Convert from UTC to Guatemala timezone (UTC-6)
                                                    const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
                                                    const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
                                                    const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
                                                    const year = guatemalaDate.getUTCFullYear();
                                                    return `${day}/${month}/${year}`;
                                                })()}
                                            {:else if column.type === 'number'}
                                                {#if column.field === 'total'}
                                                    <span class="font-semibold">
                                                        {record.total ? Number(record.total).toLocaleString() : '-'}
                                                    </span>
                                                {:else}
                                                    <span class="font-semibold">
                                                        {record[column.field] != null && !isNaN(record[column.field])
                                                            ? Number(record[column.field]).toLocaleString()
                                                            : '-'}
                                                    </span>
                                                {/if}
                                            {:else if column.field === 'escoltas_nombres'}
                                                {#if record.escoltas_nombres}
                                                    {record.escoltas_nombres}
                                                {:else}
                                                    <span class="text-gray-400 italic">-</span>
                                                {/if}
                                            {:else}
                                                {record[column.field]}
                                            {/if}
                                        </td>
                                        {/each}

                                    </tr>
                                {/each}
                            </tbody>
                        </table>

                        <!-- Pagination -->
                        {#if totalPages > 1}
                            <div class="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                                <div class="flex items-center space-x-2">
                                    <span class="text-sm text-gray-700">Mostrar:</span>
                                    <select
                                        bind:value={itemsPerPage}
                                        class="px-2 py-1 border rounded text-sm"
                                        on:change={() => currentPage = 1}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <span class="text-sm text-gray-700">por página</span>
                                </div>
                                
                                <div class="flex items-center space-x-1">
                                    <button
                                        on:click={() => goToPage(1)}
                                        disabled={currentPage === 1}
                                        class="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                    >
                                        <i class="fas fa-angle-double-left"></i>
                                    </button>
                                    <button
                                        on:click={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        class="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                    >
                                        <i class="fas fa-angle-left"></i>
                                    </button>
                                    
                                    {#each Array(Math.min(5, totalPages)) as _, i}
                                        <button
                                            on:click={() => goToPage(i + 1)}
                                            class="px-3 py-1 text-sm border rounded {currentPage === (i + 1) ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}"
                                        >
                                            {i + 1}
                                        </button>
                                    {/each}
                                    
                                    <button
                                        on:click={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        class="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                    >
                                        <i class="fas fa-angle-right"></i>
                                    </button>
                                    <button
                                        on:click={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        class="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                    >
                                        <i class="fas fa-angle-double-right"></i>
                                    </button>
                                </div>
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div> 